import { useQuery } from '@tanstack/react-query';
import { listMyCommunicationAssignments } from '../../../services/api/communicationLearning';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useTutorQueryIdentity } from '../../tutor/hooks/useTutorQueryIdentity';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import { communicationAssignmentsQueryKey } from '../queryKeys';
import { isDashboardIdentityReady } from '../utils/dashboardQueryGating';

export const COMMUNICATION_ASSIGNMENTS_QUERY_KEY = [
  'student-dashboard',
  'communication-assignments',
] as const;

const STALE_TIME_MS = 5 * 60 * 1000;

export function useCommunicationAssignments(options?: { enabled?: boolean }) {
  const { isSchoolFeatureEnabled, loading: featureLoading } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const identityReady = isDashboardIdentityReady(membershipKey, studentKey);

  const enabled =
    (options?.enabled ?? true) &&
    isSchoolFeatureEnabled('communication_tutor') &&
    !featureLoading &&
    !labelsLoading &&
    !labels.isMalay &&
    identityReady;

  const queryKey = identityReady
    ? communicationAssignmentsQueryKey({ membershipKey, studentKey })
    : [...COMMUNICATION_ASSIGNMENTS_QUERY_KEY, 'disabled'];

  const query = useQuery<{ ok: boolean; items: unknown[] }, Error>({
    queryKey,
    queryFn: listMyCommunicationAssignments,
    enabled,
    staleTime: STALE_TIME_MS,
  });

  return {
    items: (query.data?.items as Record<string, unknown>[]) ?? [],
    loading: enabled && query.isLoading,
    error: query.error,
    reload: () => query.refetch(),
  };
}
